import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { LayoutGrid, Sun, Moon, ArrowLeft, Settings } from 'lucide-react';
import { CalendarSyncCard } from '../components/settings/CalendarSyncCard';
import { ChangeEmailCard } from '../components/settings/ChangeEmailCard';
import { PasswordCard } from '../components/settings/PasswordCard';
import { DataPrivacyCard } from '../components/settings/DataPrivacyCard';
import { ExportDataCard } from '../components/settings/ExportDataCard';
import { InstallAppCard } from '../components/settings/InstallAppCard';
import { AccountCard } from '../components/settings/AccountCard';

export const SettingsPage = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const hasPassword = user && !user.google_only;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Settings className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold font-['Manrope']">Settings</span>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </header>

      <main className="flex-1 p-4 md:p-6 max-w-2xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Profile */}
          <Card className="glass border-white/20 dark:border-white/10">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Name</Label>
                <p className="font-medium">{user?.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium">{user?.email}</p>
              </div>
            </CardContent>
          </Card>

          <CalendarSyncCard />
          {hasPassword && <ChangeEmailCard user={user} />}
          <PasswordCard />

          {/* Appearance */}
          <Card className="glass border-white/20 dark:border-white/10">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Appearance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">Toggle dark/light theme</p>
                </div>
                <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="dark-mode-switch" />
              </div>
            </CardContent>
          </Card>

          <DataPrivacyCard user={user} />
          <ExportDataCard />
          <InstallAppCard />
          <AccountCard />

          <div className="text-center py-2">
            <a href="/privacy" className="text-sm text-primary hover:underline" data-testid="settings-privacy-link">
              Privacy Notice
            </a>
          </div>
        </motion.div>
      </main>
    </div>
  );
};
